import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { compose, tap } from 'ramda'
import dayjs from 'dayjs'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { AddAssignmentIcon, SubmitIcon } from 'src/v3/core/icons'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import theme from 'src/theme'
import axios, { pluckData } from 'src/utilities/http-client'
import CourseList from 'src/v3/manage/components/course-list'
import UserList from 'src/v3/manage/components/user-list'
import Loading from 'src/v3/components/loading'
import {
  fetchGroups,
  selectGroupsWithDefaults,
  selectGroupsStatus,
  selectActiveLinkedService,
  selectUsersStatus,
  fetchUsers,
  selectUsersByLinkedService,
  fetchAssignments,
  fetchTrainingSeries,
  selectTrainingSeriesByLinkedService,
  selectTrainingSeriesStatus,
  fetchCourses,
  selectCourses,
  selectCoursesStatus,
  fetchLicenses,
  selectLicensesStatus,
} from '../manage.slice'

let createAssignment = (
  linkedServiceID: string,
  assignmentName: string,
  dueDate: any,
  userIDs: string[],
  courseIDs: string[],
  groupIDs: string[] = [],
) =>
  axios
    .post<{ id: string }>('/api/v2/manage/assignment', {
      linkedServiceID,
      name: assignmentName || `Assignment from ${dayjs().format('MM/DD/YYYY')}`,
      dueDate,
      groupIDs,
      userIDs,
      courseIDs,
    })
    .then(pluckData)

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let [assignmentName, setAssignmentName] = useState<string>('')
  let [checkedUsers, setCheckedUsers] = useState<string[]>([])
  let [checkedCourses, setCheckedCourses] = useState<string[]>([])
  let [dueDate, setDueDate] = useState<any>(null)
  let [saving, setSaving] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let activeLinkedService = useAppSelector(selectActiveLinkedService)

  let groups = useAppSelector(selectGroupsWithDefaults(activeLinkedService))
  let groupsStatus = useAppSelector(selectGroupsStatus)

  let users = useAppSelector(selectUsersByLinkedService(activeLinkedService))
  let usersStatus = useAppSelector(selectUsersStatus)

  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)

  let trainingSeries = useAppSelector(
    selectTrainingSeriesByLinkedService(activeLinkedService),
  )
  let trainingSeriesStatus = useAppSelector(selectTrainingSeriesStatus)

  let licensesStatus = useAppSelector(selectLicensesStatus)

  let isInvalidDueDate =
    dueDate === 'Invalid Date' || dayjs(dueDate).isBefore(dayjs())

  let handleDateChange = compose(tap(setDueDate), (d: any) => dayjs(d))

  useEffect(() => {
    if (groupsStatus === 'uninitialized') {
      dispatch(fetchGroups())
    }
  }, [groupsStatus])

  useEffect(() => {
    if (usersStatus === 'uninitialized') {
      dispatch(fetchUsers())
    }
  }, [usersStatus])

  useEffect(() => {
    if (trainingSeriesStatus === 'uninitialized') {
      dispatch(fetchTrainingSeries())
    }
  }, [trainingSeriesStatus])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  useEffect(() => {
    if (
      activeLinkedService !== 'unselected' &&
      (!licensesStatus[activeLinkedService] ||
        licensesStatus[activeLinkedService] === 'uninitialized')
    ) {
      dispatch(fetchLicenses(activeLinkedService))
    }
  }, [licensesStatus, activeLinkedService])

  if (activeLinkedService === 'unselected') {
    navigate('/manage/assignments')
  }

  return (
    <>
      <Box>
        <div className="flex items-center justify-center my-2">
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <AddAssignmentIcon />
          </Avatar>
          <Typography
            className="pl-2"
            variant="h4"
            style={{
              fontFamily: 'smileAndWave',
            }}
          >
            Create Assignment
          </Typography>
        </div>
        <Divider className="my-3" />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" textAlign="center">
            Assignment Name
          </Typography>
          <TextField
            sx={{ width: 300 }}
            variant="outlined"
            className="mt-2 mb-3"
            label="Name"
            name="name"
            type="text"
            fullWidth
            value={assignmentName}
            onChange={(event: React.ChangeEvent) => {
              setAssignmentName((event.target as HTMLInputElement).value)
            }}
          />
          <Typography variant="h5" textAlign="center">
            Due Date
          </Typography>
          <div className="mt-2 mb-3">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{ width: 300 }}
                label="Complete By Date"
                value={dueDate}
                onChange={handleDateChange}
              />
            </LocalizationProvider>
          </div>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            columnGap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" textAlign="center">
              Select Courses
            </Typography>
            <CourseList
              courses={courses}
              trainingSeries={trainingSeries}
              checkbox={{
                onClick: setCheckedCourses,
                checked: checkedCourses,
              }}
            />
          </Box>
          <Box>
            <Typography variant="h5" textAlign="center">
              Select Users
            </Typography>
            <UserList
              users={users}
              groups={groups}
              checkbox={{
                onClick: setCheckedUsers,
                checked: checkedUsers,
              }}
            />
          </Box>
        </Box>
        <div className="flex justify-center">
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<SubmitIcon />}
            disabled={
              !assignmentName ||
              !dueDate ||
              isInvalidDueDate ||
              activeLinkedService === 'unselected'
            }
            onClick={() => {
              setSaving(true)
              createAssignment(
                activeLinkedService,
                assignmentName,
                dueDate,
                checkedUsers,
                checkedCourses,
                [],
              )
                .then((data) => {
                  dispatch(fetchAssignments()).then(() =>
                    navigate(`/manage/assignments/${data.id}`),
                  )
                })
                .catch((err) => {
                  setErrorMessage(err?.response?.data)
                  setSaving(false)
                })
            }}
          >
            Submit
          </Button>
          <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
            <Alert
              variant="filled"
              onClose={() => setErrorMessage('')}
              severity="error"
            >
              <strong>Error Creating Assignment: </strong>
              {errorMessage}
            </Alert>
          </Snackbar>
        </div>
      </Box>
      <Loading
        entities={[
          coursesStatus,
          groupsStatus,
          trainingSeriesStatus,
          usersStatus,
        ]}
        loading={saving}
      />
    </>
  )
}
